import React, { useState, useEffect } from "react";
import {
  Pencil,
  Save,
  X,
  Plus,
  Target,
  GripVertical,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCourse } from "../../../../context/CourseContext";
import { useUtilityContext } from "../../../../context/UtilityContext";
import { updateCourse } from "../../../../services/course.service";
import toast from "react-hot-toast";
import AssessmentPlan from "./AssessmentPlan";
import { useParams } from "react-router-dom";
import {
  card,
  cardTitle,
  microLabel,
  bodySecondary,
  accentBarSection,
  dividerInner,
  emptyState,
  motionFadeUp,
  motionStaggerChild,
  iconSize,
  pageTitle,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";

// ---------------------------------------------------------------------------
// SECTION WRAPPER — consistent card with header + edit toggle
// ---------------------------------------------------------------------------

const Section = ({
  title,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  isSaving = false,
  children,
  className,
  staggerIndex = 0,
}) => (
  <motion.div
    {...motionStaggerChild(staggerIndex, 0.1)}
    className={cn(card, "overflow-hidden", className)}
  >
    <div className={cn("px-5 py-4 border-b flex items-center justify-between", dividerInner)}>
      <h2 className={cardTitle}>{title}</h2>

      {!isEditing ? (
        <button
          onClick={onEdit}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-accent1/10 text-accent1 text-xs font-bold uppercase tracking-wider hover:bg-accent1/20 transition-colors"
        >
          <Pencil className={iconSize.sm} />
          Edit
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            disabled={isSaving}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-bold uppercase tracking-wider hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            <X className={iconSize.sm} />
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={isSaving}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm bg-accent1 text-white text-xs font-bold uppercase tracking-wider hover:bg-accent1/90 transition-colors disabled:opacity-50"
          >
            <Save className={iconSize.sm} />
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      )}
    </div>
    <div className="p-5">{children}</div>
  </motion.div>
);

// ---------------------------------------------------------------------------
// MAIN
// ---------------------------------------------------------------------------

const CourseBrief = () => {
  const {
    courseData,
    totalCredits,
    setCourseData,
  } = useCourse();

  const { courseID: urlCourseID } = useParams();
  const { selectedCourseId } = useUtilityContext();
  const courseID = urlCourseID || selectedCourseId;

  const [activeSection, setActiveSection] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [tempAboutCourse, setTempAboutCourse] = useState(courseData.aboutCourse);
  const [tempCreditPoints, setTempCreditPoints] = useState({ ...courseData.creditPoints });
  const [tempOutcomes, setTempOutcomes] = useState([]);

  useEffect(() => {
    if (activeSection === "outcomes") {
      setTempOutcomes([...courseData.learningOutcomes]);
    }
  }, [activeSection, courseData.learningOutcomes]);

  const handleSave = async (section) => {
    let updatedCourseData;
    switch (section) {
      case "about":
        updatedCourseData = { ...courseData, aboutCourse: tempAboutCourse };
        break;
      case "credits": {
        const parsedCredits = {};
        Object.entries(tempCreditPoints).forEach(([type, value]) => {
          parsedCredits[type] = parseInt(value) || 0;
        });
        updatedCourseData = { ...courseData, creditPoints: parsedCredits };
        break;
      }
      case "outcomes":
        updatedCourseData = { ...courseData, learningOutcomes: tempOutcomes };
        break;
      default:
        return;
    }

    setIsSaving(true);
    try {
      const response = await updateCourse(courseID, updatedCourseData);
      setCourseData(response);
      setActiveSection(null);
      toast.success("Saved successfully");
    } catch (error) {
      console.error("Error saving course:", error);
      toast.error(error.response?.data?.error || "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = (section) => {
    switch (section) {
      case "about":
        setTempAboutCourse(courseData.aboutCourse);
        break;
      case "credits":
        setTempCreditPoints({ ...courseData.creditPoints });
        break;
      case "outcomes":
        setTempOutcomes([...courseData.learningOutcomes]);
        break;
    }
    setActiveSection(null);
  };

  const updateOutcomeNumbers = (outcomes) =>
    outcomes.map((outcome, index) => {
      const text = outcome.replace(/^LO\d+:\s*/, "");
      return `LO${index + 1}: ${text}`;
    });

  const handleDeleteOutcome = (index) => {
    setTempOutcomes((prev) => updateOutcomeNumbers(prev.filter((_, i) => i !== index)));
  };

  const handleAddOutcome = () => {
    setTempOutcomes((prev) => updateOutcomeNumbers([...prev, `LO${prev.length + 1}: `]));
  };

  const handleUpdateOutcome = (index, value) => {
    setTempOutcomes((prev) => {
      const next = [...prev];
      const text = value.replace(/^LO\d+:\s*/, "");
      next[index] = `LO${index + 1}: ${text}`;
      return next;
    });
  };

  // Credit point entries
  const creditEntries = Object.entries(
    activeSection === "credits" ? tempCreditPoints : courseData.creditPoints
  );
  const currentTotalCredits =
    activeSection === "credits"
      ? Object.values(tempCreditPoints).reduce((sum, val) => sum + (parseInt(val) || 0), 0)
      : totalCredits;

  return (
    <motion.div {...motionFadeUp} className="mx-auto space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={pageTitle}>Course Overview</h1>
          <p className={cn(bodySecondary, "mt-1")}>
            Manage your course information and learning outcomes
          </p>
          <div className={cn(accentBarSection, "mt-3")} />
        </div>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* ABOUT COURSE — full width hero */}
      {/* ----------------------------------------------------------------- */}
      <Section
        title="About The Course"
        isEditing={activeSection === "about"}
        onEdit={() => setActiveSection("about")}
        onSave={() => handleSave("about")}
        onCancel={() => handleCancel("about")}
        isSaving={isSaving}
        staggerIndex={0}
      >
        {activeSection === "about" ? (
          <textarea
            value={tempAboutCourse}
            onChange={(e) => setTempAboutCourse(e.target.value)}
            className="w-full min-h-[180px] p-4 rounded-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white text-sm leading-relaxed focus:ring-2 focus:ring-accent1/30 focus:border-accent1 transition-all resize-y"
            placeholder="Enter course description..."
          />
        ) : (
          <p
            className={cn(
              "leading-relaxed text-sm",
              courseData.aboutCourse
                ? "text-gray-700 dark:text-gray-200"
                : cn(bodySecondary, "italic")
            )}
          >
            {courseData.aboutCourse ||
              "No course description added yet. Click Edit to add content."}
          </p>
        )}
      </Section>

      {/* TWO COLUMN — Credits + Assessment */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ----------------------------------------------------------------- */}
        {/* CREDIT POINTS — horizontal bars */}
        {/* ----------------------------------------------------------------- */}
        <Section
          title="Credit Points"
          isEditing={activeSection === "credits"}
          onEdit={() => setActiveSection("credits")}
          onSave={() => handleSave("credits")}
          onCancel={() => handleCancel("credits")}
          isSaving={isSaving}
          staggerIndex={1}
        >
          <div className="space-y-4">
            {creditEntries.map(([type, value]) => {
              const pct =
                currentTotalCredits > 0
                  ? Math.round((parseInt(value) / currentTotalCredits) * 100)
                  : 0;

              return (
                <div key={type}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={microLabel}>{type}</span>
                    {activeSection === "credits" ? (
                      <input
                        type="number"
                        value={value}
                        onChange={(e) =>
                          setTempCreditPoints((prev) => ({
                            ...prev,
                            [type]: parseInt(e.target.value) || 0,
                          }))
                        }
                        className="w-16 text-right text-sm font-black tracking-tight bg-transparent border-b-2 border-accent1/30 focus:border-accent1 text-accent1 outline-none transition-colors py-0.5"
                        min="0"
                      />
                    ) : (
                      <span className="text-sm font-black tracking-tight text-accent1">
                        {value}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Total */}
            <div className="pt-3 mt-3 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <span className={cn(microLabel, "text-gray-900 dark:text-white")}>
                  Total Credits
                </span>
                <span className="text-xl font-black tracking-tight text-accent1">
                  {currentTotalCredits}
                </span>
              </div>
            </div>
          </div>
        </Section>

        {/* ----------------------------------------------------------------- */}
        {/* ASSESSMENT PLAN */}
        {/* ----------------------------------------------------------------- */}
        <motion.div {...motionStaggerChild(2, 0.1)}>
          <AssessmentPlan />
        </motion.div>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* LEARNING OUTCOMES — numbered list */}
      {/* ----------------------------------------------------------------- */}
      <Section
        title="Learning Outcomes"
        isEditing={activeSection === "outcomes"}
        onEdit={() => setActiveSection("outcomes")}
        onSave={() => handleSave("outcomes")}
        onCancel={() => handleCancel("outcomes")}
        isSaving={isSaving}
        staggerIndex={3}
      >
        {activeSection === "outcomes" ? (
          <div className="space-y-2.5">
            <AnimatePresence>
              {tempOutcomes.map((outcome, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-2.5"
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-sm bg-accent1/10 dark:bg-accent1/20 flex items-center justify-center">
                    <span className="text-xs font-black text-accent1">
                      {index + 1}
                    </span>
                  </div>
                  <input
                    value={outcome}
                    onChange={(e) => handleUpdateOutcome(index, e.target.value)}
                    className="flex-1 px-3 py-2.5 rounded-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-accent1/30 focus:border-accent1 transition-all"
                  />
                  <button
                    onClick={() => handleDeleteOutcome(index)}
                    className="flex-shrink-0 p-2 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-sm transition-colors"
                  >
                    <X className={iconSize.md} />
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>

            <button
              onClick={handleAddOutcome}
              className={cn(
                emptyState,
                "w-full !p-3 text-sm font-medium text-gray-500 dark:text-gray-400 hover:border-accent1 hover:text-accent1 hover:bg-accent1/5 dark:hover:bg-accent1/10 transition-all flex items-center justify-center"
              )}
            >
              <Plus className={cn(iconSize.md, "mr-2")} />
              Add Learning Outcome
            </button>
          </div>
        ) : (
          <div>
            {courseData.learningOutcomes.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                {courseData.learningOutcomes.map((outcome, index) => (
                  <motion.div
                    key={index}
                    {...motionStaggerChild(index, 0.1)}
                    className="flex items-start gap-3 p-3.5 bg-gray-50 dark:bg-gray-900 rounded-sm border border-gray-100 dark:border-gray-700/50 hover:border-accent1/20 transition-colors"
                  >
                    <div className="flex-shrink-0 w-7 h-7 rounded-sm bg-accent1/10 dark:bg-accent1/20 flex items-center justify-center mt-0.5">
                      <span className="text-[10px] font-black text-accent1">
                        {index + 1}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                      {outcome}
                    </p>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className={emptyState}>
                <Target
                  className={cn(
                    iconSize.empty,
                    "text-gray-300 dark:text-gray-600 mx-auto mb-3"
                  )}
                />
                <p className={bodySecondary}>
                  No learning outcomes added yet. Click Edit to add outcomes.
                </p>
              </div>
            )}
          </div>
        )}
      </Section>
    </motion.div>
  );
};

export default CourseBrief;
